import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BGCommon from './BGCommon';
import useScrollToTop from 'src/hooks/use-Scroll-to-top';
import BGTopicsBar from './BGTopicsBar';
import BGPostList from './BGPostList';
import {
  BlogGenerationResponse,
  Blueprint,
  BlogPost,
  BlogRunResponse
} from '../../types/blog-generation-agent.types';
import useBlogGenerationMutation from '../hooks/use-blog-generation-mutation';
import useGetGeneratedBlogByBlueprint from '../hooks/use-get-generated-blog-by-blueprint';
import useGenerateBlog from '../api/use-generate-blog';
import { GenerateBlogPostDialog } from './GenerateBlogPostDialog';
import { GenerateBlogFromBlueprintDialog } from './GenerateBlogFromBlueprintDialog';
import { ViewBlogPostDialog } from './ViewBlogPostDialog';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

interface BlogGenerationProps {
  blogBlueprints: BlogGenerationResponse;
  isLoading: boolean;
  isPending: boolean;
  siteSoftDeleted: boolean;
}

const BlogGeneration = ({
  blogBlueprints,
  isLoading,
  isPending,
  siteSoftDeleted
}: BlogGenerationProps) => {
  const location = useLocation();
  const { targetRef } = useScrollToTop({ deps: [] });

  const { _id: siteId, user_id: userId } = useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails) ?? {};

  const [searchQuery, setSearchQuery] = useState('');
  const [topics] = blogBlueprints?.runs || [];

  const {
    dialogOpen,
    setDialogOpen,
    topics: mutationTopics,
    setTopics: setMutationTopics,
    blogBlueprintsMutation,
    isMutationPending,
    handleEnableBlogGeneration
  } = useBlogGenerationMutation({
    topics: topics?.topics || []
  });

  // Handle incoming prefill from Content Agent
  useEffect(() => {
    const prefill = (location.state as any)?.prefill;
    if (prefill) {
      const kw = prefill.target_keyword || prefill.topic;
      if (kw) {
        setMutationTopics([kw]);
        setDialogOpen(true);
      }
    }
  }, [location.state, setMutationTopics, setDialogOpen]);

  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(
    null
  );
  const [generateDialogOpen, setGenerateDialogOpen] = useState<boolean>(false);

  const [generatedPost, setGeneratedPost] = useState<BlogPost | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);

  // Fetch a generated blog post by blueprint ID (for "View Post")
  const [viewBlueprintId, setViewBlueprintId] = useState<string | null>(null);
  const { data: fetchedBlogData, isLoading: isFetchingBlog } =
    useGetGeneratedBlogByBlueprint(viewBlueprintId);
  const fetchedPost: BlogPost | null = fetchedBlogData?.post ?? null;

  // Track which blueprints are currently being generated (optimistic UI)
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());

  const removeGeneratingId = useCallback((id: string) => {
    setGeneratingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const generateBlogMutation = useGenerateBlog();

  const handleStartGeneration = useCallback(
    (blueprint: Blueprint, suggestion?: string) => {
      const blueprintId = blueprint._id;
      setGeneratingIds((prev) => new Set(prev).add(blueprintId));

      generateBlogMutation.mutate(
        {
          blueprint,
          parent_run_id: topics?.run_id || topics?._id,
          suggestion
        },
        {
          onSuccess: (data: BlogRunResponse) => {
            removeGeneratingId(blueprintId);
            if (data?.result?.post) {
              setViewBlueprintId(null);
              setGeneratedPost(data.result.post);
              setViewDialogOpen(true);
            }
          },
          onError: () => {
            removeGeneratingId(blueprintId);
          }
        }
      );
    },
    [topics?.run_id, topics?._id, generateBlogMutation, removeGeneratingId]
  );

  // Automatically clean up generatingIds when backend polling updates blueprint status
  useEffect(() => {
    const blueprints = topics?.blueprints || [];
    if (blueprints.length === 0 || generatingIds.size === 0) return;

    setGeneratingIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const bp of blueprints) {
        if (
          bp.generation_status === 'generated' ||
          bp.generation_status === 'failed'
        ) {
          if (next.has(bp._id)) {
            next.delete(bp._id);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [topics?.blueprints, generatingIds.size]);

  // Overlay optimistic 'processing' status on posts that are currently generating
  const displayPosts = useMemo(() => {
    const blueprints = topics?.blueprints || [];
    if (generatingIds.size === 0) return blueprints;
    return blueprints.map((bp) =>
      generatingIds.has(bp._id)
        ? { ...bp, generation_status: 'processing' as const }
        : bp
    );
  }, [topics?.blueprints, generatingIds]);

  return (
    <div
      className="-mt-8 pt-8 pb-6"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
      ref={targetRef as React.RefObject<HTMLDivElement>}
    >
      <div aria-label="blog-generation-common-header-and-progress">
        <BGCommon />
      </div>

      {isLoading ||
      isPending ||
      blogBlueprintsMutation.isPending ||
      isMutationPending ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            minHeight: 'calc(100vh - 173px)',
            textAlign: 'center'
          }}
        >
          <CircularProgress size={64} />
          <p className="font-semibold text-lg text-[#1D2939] mt-2">
            Orbit AI Blog Generation is running...
          </p>
          <p className="font-normal text-sm text-[#667085] max-w-md">
            This may take a few moments while we craft topic ideas and generate
            blog post blueprints.
          </p>
        </div>
      ) : (
        <>
          <div>
            <p className="text-primary font-semibold text-3xl">
              Blog Post Agent
            </p>
          </div>
          <div aria-label="blog-generation-topics">
            <BGTopicsBar
              topics={topics?.topics || []}
              topicCount={topics?.topics?.length || 0}
              onManageTopics={() => {
                setMutationTopics(topics?.topics || []);
                setDialogOpen(true);
              }}
              siteSoftDeleted={siteSoftDeleted}
            />
          </div>

          <div className="my-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-[#A09DAB]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search blueprints by title"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="shadow-sm pl-10 pr-4 py-2 w-full max-w-sm text-sm bg-white border border-borderLight rounded-lg focus:outline-none focus:border-[var(--color-text-purple)] transition-colors duration-200"
              />
            </div>
          </div>

          <div aria-label="blog-generation-posts">
            <BGPostList
              posts={displayPosts}
              searchQuery={searchQuery}
              onGeneratePost={(post) => {
                setSelectedBlueprint(post);
                setGenerateDialogOpen(true);
              }}
              onViewPost={(post) => {
                setGeneratedPost(null);
                setViewBlueprintId(post._id);
                setViewDialogOpen(true);
              }}
            />
          </div>
        </>
      )}

      <GenerateBlogPostDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        keywords={mutationTopics}
        setKeywords={setMutationTopics}
        onEnable={(keywords) =>handleEnableBlogGeneration(keywords, { siteId, userId })}
        title="Manage Topics"
        submitText="Save"
      />

      {selectedBlueprint && (
        <GenerateBlogFromBlueprintDialog
          open={generateDialogOpen}
          onClose={() => {
            setGenerateDialogOpen(false);
            setSelectedBlueprint(null);
          }}
          blueprint={selectedBlueprint}
          parentRunId={topics?.run_id || topics?._id}
          onGenerate={handleStartGeneration}
        />
      )}

      <ViewBlogPostDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewBlueprintId(null);
          setGeneratedPost(null);
        }}
        post={generatedPost ?? fetchedPost}
        isLoading={viewBlueprintId ? isFetchingBlog : false}
      />
    </div>
  );
};

export default BlogGeneration;
